import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { Camera } from "lucide-react";

import {
  HookFormInput,
  HookFormBaseDropdown,
  HookFormBaseUpload,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseError from "@/components_v2/FormFields/BaseError";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import {
  Honorifics,
  MAX_PROFILE_PICTURE_FILE_SIZE,
  SUPPORTED_PROFILE_PICTURE_FILE_FORMATS,
} from "@/shared/utils/constants";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { PersonalDetailsStepProps, RegisterFormValues } from "../../types";
import BaseDivider from "@/components_v2/Divider/BaseDivider";

import "../../styles.css";

const PersonalDetailsStep = <T extends RegisterFormValues>({
  form,
  countryOptions,
  areaOfExpertiseOptions,
  title,
  description,
}: PersonalDetailsStepProps<T>) => {
  const { t } = useTranslation();

  const accountForm = form as unknown as UseFormReturn<RegisterFormValues>;
  const profilePicture = accountForm.watch("user.profilePicture");
  const profilePictureError = accountForm.formState.errors.user?.profilePicture;

  const showProfilePicturePreview = useMemo(() => {
    if (!profilePicture) {
      return false;
    }

    if (typeof profilePicture === "string") {
      return true;
    }

    return SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.some(
      (format) => format.contentType === profilePicture.type,
    );
  }, [profilePicture]);

  const currentProfilePicture = useMemo(() => {
    if (!showProfilePicturePreview) {
      return null;
    }

    if (profilePicture instanceof File) {
      return URL.createObjectURL(profilePicture);
    }

    return profilePicture || null;
  }, [profilePicture, showProfilePicturePreview]);

  const handleRemoveProfilePicture = () => {
    accountForm.setValue("user.profilePicture", null);
  };

  return (
    <div className="register-account-step flex flex-col flex-1 gap-400">
      <div className="flex flex-col gap-200">
        <h2 className="register__title header-m-medium">{title}</h2>
        <p className="register__description body-m-regular">{description}</p>
      </div>

      <div className="register-form-container flex flex-col flex-1 gap-400 text-start">
        <div className="flex gap-300 items-center">
          <div className="register-profile-picture-upload-container">
            <div
              className={`register-profile-picture-upload-circle ${currentProfilePicture ? "register-profile-picture-upload-circle--filled" : ""}`}
              style={{
                backgroundImage: currentProfilePicture
                  ? `url(${currentProfilePicture})`
                  : "none",
              }}
            >
              <HookFormBaseUpload
                name="user.profilePicture"
                control={form.control}
                shouldShowValues={false}
                showError={false}
                accept={SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.map(
                  (format) => format.contentType,
                ).join(",")}
                maxFileSize={MAX_PROFILE_PICTURE_FILE_SIZE}
                icon={<Camera size={32} color="var(--primary-600)" />}
              />
            </div>

            {showProfilePicturePreview && (
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.DANGER}
                leadingIcon={
                  <BaseSvgRenderer
                    component={DeleteIcon}
                    width="20"
                    height="20"
                    stroke="var(--base-white)"
                  />
                }
                onClick={handleRemoveProfilePicture}
                className="register-profile-picture-remove-btn"
                aria-label={t("auth.labels.remove")}
              />
            )}
          </div>

          <div className="flex flex-col gap-100">
            <p className="register-profile-picture-upload-text body-s-medium">
              {t("auth.labels.profilePictureTitle")}
            </p>

            <p className="register-profile-picture-upload-description body-s-regular">
              {t("auth.labels.profilePictureDescription")}
            </p>

            <BaseError error={profilePictureError} />
          </div>
        </div>

        <BaseDivider />

        <h5 className="register__subtitle body-s-medium">
          {t("auth.labels.personalInformation")}
        </h5>

        <div className="form-input-container">
          <div className="flex gap-200">
            <FormFieldWrapper label={t("auth.labels.honorific")} required>
              <HookFormBaseDropdown
                className="register-honorific-dropdown"
                name="profile.honorific"
                placeholder={t("auth.placeholders.selectHonorific")}
                options={Honorifics.asList()}
                control={form.control}
              />
            </FormFieldWrapper>

            <FormFieldWrapper label={t("auth.labels.firstName")} required>
              <HookFormInput
                className="register-name-input"
                name="user.firstName"
                placeholder={t("auth.placeholders.enterFirstName")}
                control={form.control}
              />
            </FormFieldWrapper>
          </div>

          <FormFieldWrapper label={t("auth.labels.lastName")} required>
            <HookFormInput
              className="register-name-input"
              name="user.lastName"
              placeholder={t("auth.placeholders.enterLastName")}
              control={form.control}
            />
          </FormFieldWrapper>
        </div>

        <BaseDivider />

        <h5 className="register__subtitle body-s-medium">
          {t("auth.labels.location")}
        </h5>

        <div className="flex gap-200 flex-wrap">
          <FormFieldWrapper label={t("auth.labels.city")} required>
            <HookFormInput
              name="profile.city"
              placeholder={t("auth.placeholders.enterCity")}
              control={form.control}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label={t("auth.labels.country")} required>
            <HookFormBaseDropdown
              name="profile.country"
              placeholder={t("auth.placeholders.selectCountry")}
              options={countryOptions}
              control={form.control}
            />
          </FormFieldWrapper>
        </div>

        <BaseDivider />

        <h5 className="register__subtitle body-s-medium">
          {t("auth.labels.professionalDetails")}
        </h5>

        <div className="form-input-container">
          <FormFieldWrapper label={t("auth.labels.jobTitle")} required>
            <HookFormInput
              name="profile.jobTitle"
              placeholder={t("auth.placeholders.enterJobTitle")}
              control={form.control}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label={t("auth.labels.department")} required>
            <HookFormInput
              name="profile.department"
              placeholder={t("auth.placeholders.enterDepartment")}
              control={form.control}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label={t("auth.labels.areasOfExpertise")}>
            <HookFormBaseDropdown
              multiple
              name="profile.areasOfExpertise"
              placeholder={t("auth.placeholders.selectAreasOfExpertise")}
              options={areaOfExpertiseOptions}
              control={form.control}
            />
          </FormFieldWrapper>
        </div>

        <BaseDivider />

        <h5 className="register__subtitle body-s-medium">
          {t("auth.labels.accountSecurity")}
        </h5>

        <div className="form-input-container">
          <FormFieldWrapper label={t("auth.labels.password")} required>
            <HookFormInput
              type="password"
              name="user.password"
              control={form.control}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label={t("auth.labels.confirmPassword")} required>
            <HookFormInput
              type="password"
              name="confirmPassword"
              control={form.control}
            />
          </FormFieldWrapper>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetailsStep;
